import { Box } from '@mantine/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type Heading, useIntersectionObserver } from 'src/hooks'

import { HeadingItem } from '../HeadingItem'

const OVERVIEW: Heading = { id: 'overview', text: 'Overview', level: 2 }

const flattenHeadings = (items: Heading[]): Heading[] =>
  items.flatMap((h) => [h, ...flattenHeadings(h.children ?? [])])

type Props = {
  headings: Heading[]
}

export const TableOfContents = ({ headings }: Props) => {
  const [activeId, setActiveId] = useState<string>('overview')
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const allHeadings = useMemo(() => [OVERVIEW, ...headings], [headings])
  const flatHeadings = useMemo(
    () => flattenHeadings(allHeadings),
    [allHeadings]
  )

  useIntersectionObserver(
    setActiveId,
    flatHeadings.map((h) => h.id)
  )

  useEffect(() => {
    const container = containerRef.current
    const item = itemRefs.current[activeId]

    if (!container || !item) return

    const containerTop = container.scrollTop
    const containerBottom = containerTop + container.clientHeight
    const itemTop = item.offsetTop
    const itemBottom = itemTop + item.offsetHeight

    if (itemTop < containerTop) {
      container.scrollTop = itemTop
    } else if (itemBottom > containerBottom) {
      container.scrollTop = itemBottom - container.clientHeight
    }
  }, [activeId])

  const registerRef = useCallback((id: string, el: HTMLDivElement | null) => {
    itemRefs.current[id] = el
  }, [])

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id)

    if (!el) return

    const top = el.getBoundingClientRect().top + window.scrollY - 120

    window.scrollTo({ top, behavior: 'smooth' })
  }, [])

  if (!headings?.length) return null

  return (
    <Box
      ref={containerRef}
      pos="relative"
      mah="calc(100vh - 300px)"
      style={{
        borderLeft: '2px solid var(--mantine-color-gray-3)',
        overflow: 'hidden'
      }}
    >
      {allHeadings.map((heading) => (
        <HeadingItem
          key={heading.id}
          heading={heading}
          depth={0}
          activeId={activeId}
          registerRef={registerRef}
          onScrollTo={scrollTo}
        />
      ))}
    </Box>
  )
}
